import React, {
    Fragment,
    forwardRef,
    useCallback,
    useMemo,
    useRef,
    useState,
    useLayoutEffect,
} from "react";
import {createPortal} from "react-dom";
import {Menu, Transition} from "@headlessui/react";
import {ChevronDownIcon} from "@heroicons/react/solid";
import {CheckIcon, DocumentSearchIcon, PencilAltIcon, XIcon} from "@heroicons/react/outline";
import {useTranslation} from "react-i18next";
import {CANCELED, CONFIRMED, DRAFT, REVIEW} from "../../../constants/config";
import Loader from "../loader/loader";

const DROPDOWN_VERTICAL_OFFSET_PX = 8;
const VIEWPORT_PADDING_PX = 8;
const LG_BREAKPOINT_PX = 1024;
const LG_DROPDOWN_WIDTH_PX = 224;
const STATUS_LIST_DROPDOWN_Z_INDEX_CLASS = "z-[120]";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

const StatusListDropdown = forwardRef(({buttonRef, className = "", children, style: transitionStyles = {}, ...rest}, ref) => {
    const portalTarget = typeof document !== "undefined" ? document.body : null;
    const [dropdownStyle, setDropdownStyle] = useState(null);

    const updateDropdownPosition = useCallback(() => {
        if (!buttonRef?.current || typeof window === "undefined" || typeof document === "undefined") {
            return;
        }
        const triggerRect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth ?? document.documentElement?.clientWidth ?? triggerRect.width;
        const isLargeViewport = viewportWidth >= LG_BREAKPOINT_PX;
        const dropdownWidth = isLargeViewport ? LG_DROPDOWN_WIDTH_PX : triggerRect.width;
        const desiredLeft = isLargeViewport
            ? triggerRect.right - dropdownWidth
            : triggerRect.left;
        const maxLeft = Math.max(VIEWPORT_PADDING_PX, viewportWidth - dropdownWidth - VIEWPORT_PADDING_PX);
        const safeLeft = Math.min(Math.max(desiredLeft, VIEWPORT_PADDING_PX), maxLeft);

        setDropdownStyle({
            position: "fixed",
            top: triggerRect.bottom + DROPDOWN_VERTICAL_OFFSET_PX,
            left: safeLeft,
            width: dropdownWidth,
        });
    }, [buttonRef]);

    useLayoutEffect(() => {
        if (!portalTarget) {
            return undefined;
        }
        updateDropdownPosition();
        if (typeof window === "undefined") {
            return undefined;
        }

        const handleReposition = () => {
            updateDropdownPosition();
        };

        window.addEventListener("resize", handleReposition);
        window.addEventListener("scroll", handleReposition, true);

        return () => {
            window.removeEventListener("resize", handleReposition);
            window.removeEventListener("scroll", handleReposition, true);
        };
    }, [portalTarget, updateDropdownPosition]);

    if (!portalTarget) {
        return null;
    }

    const {style, ...restProps} = rest;
    const fallbackRect = buttonRef?.current?.getBoundingClientRect();
    const fallbackWidth = fallbackRect?.width ?? "auto";
    const resolvedStyle = {
        position: "fixed",
        top: fallbackRect ? (fallbackRect.bottom + DROPDOWN_VERTICAL_OFFSET_PX) : 0,
        left: fallbackRect?.left ?? 0,
        width: fallbackWidth,
        opacity: dropdownStyle ? 1 : 0,
        pointerEvents: dropdownStyle ? "auto" : "none",
        ...(dropdownStyle ?? {}),
        ...transitionStyles,
        ...(style ?? {}),
    };

    return createPortal(
        <div
            {...restProps}
            ref={ref}
            style={resolvedStyle}
            className={classNames(
                STATUS_LIST_DROPDOWN_Z_INDEX_CLASS,
                className,
            )}
        >
            {children}
        </div>,
        portalTarget,
    );
});


const STATUS_ALIASES = {
    cancelled: CANCELED,
};

const DEFAULT_STATUS_CONFIG = {
    baseClass: "border-gray-500 text-gray-600",
    hoverClass: "hover:bg-gray-600 hover:text-white",
    icon: DocumentSearchIcon,
};

const STATUS_CONFIG = {
    [DRAFT]: {
        baseClass: "border-gray-800 text-gray-800",
        hoverClass: "hover:bg-gray-800 hover:text-white",
        translationKey: "app.quotations.status_draft",
        defaultLabel: "Draft",
        icon: PencilAltIcon,
    },
    [REVIEW]: {
        baseClass: "border-yellow-700 text-yellow-700",
        hoverClass: "hover:bg-yellow-700 hover:text-white",
        translationKey: "app.quotations.status_review",
        defaultLabel: "Review",
        icon: DocumentSearchIcon,
    },
    negotiation: {
        baseClass: "border-yellow-700 text-yellow-700",
        hoverClass: "hover:bg-yellow-700 hover:text-white",
        translationKey: "app.quotations.status_negotiation",
        defaultLabel: "Negotiation",
        icon: DocumentSearchIcon,
    },
    [CONFIRMED]: {
        baseClass: "border-green-800 text-green-800",
        hoverClass: "hover:bg-green-800 hover:text-white",
        translationKey: "app.quotations.status_confirmed",
        defaultLabel: "Confirmed",
        icon: CheckIcon,
    },
    [CANCELED]: {
        baseClass: "border-red-800 text-red-800",
        hoverClass: "hover:bg-red-800 hover:text-white",
        translationKey: "app.quotations.status_canceled",
        defaultLabel: "Cancelled",
        icon: XIcon,
    },
    closed: {
        baseClass: "border-gray-700 text-gray-700",
        hoverClass: "hover:bg-gray-700 hover:text-white",
        translationKey: "app.quotations.status_closed",
        defaultLabel: "Closed",
        icon: CheckIcon,
    },
};

const DEFAULT_STATUS_TRANSITIONS = {
    [DRAFT]: [REVIEW, CONFIRMED, CANCELED],
    [REVIEW]: [CONFIRMED, CANCELED],
    [CONFIRMED]: [REVIEW, CANCELED],
    [CANCELED]: [DRAFT, REVIEW, CONFIRMED],
    default: [DRAFT, REVIEW, CONFIRMED, CANCELED],
};

const sanitizeStatusList = (values) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const seen = new Set();
    return values.reduce((accumulator, value) => {
        if (typeof value !== "string") {
            return accumulator;
        }

        const trimmed = value.trim();
        if (!trimmed) {
            return accumulator;
        }

        const normalized = trimmed.toLowerCase();
        const canonical = STATUS_ALIASES[normalized] ?? normalized;
        if (seen.has(canonical)) {
            return accumulator;
        }
        seen.add(canonical);
        accumulator.push({raw: trimmed, normalized: canonical});
        return accumulator;
    }, []);
};

const formatStatusLabel = (value) => {
    if (typeof value !== "string" || !value) {
        return "";
    }

    return value
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const resolveStatusPresentation = (statusValue, t) => {
    const normalized = typeof statusValue === "string" ? statusValue.toLowerCase() : "";
    const canonical = STATUS_ALIASES[normalized] ?? normalized;
    const config = STATUS_CONFIG[canonical] ?? {};

    const baseClass = config.baseClass ?? DEFAULT_STATUS_CONFIG.baseClass;
    const hoverClass = config.hoverClass ?? DEFAULT_STATUS_CONFIG.hoverClass;
    const translationKey = config.translationKey ?? (canonical ? `app.quotations.status_${canonical}` : null);
    const fallbackLabelBase = config.defaultLabel ?? formatStatusLabel(statusValue || canonical || "status");
    const IconComponent = config.icon ?? DEFAULT_STATUS_CONFIG.icon;
    const title = translationKey
        ? t(translationKey, {defaultValue: fallbackLabelBase})
        : fallbackLabelBase;

    return {
        title,
        IconComponent,
        baseClass,
        hoverClass,
    };
};

export const StatusList = ({isLoading, status, onChangeStatus, isEditable = true, statuses}) => {
    const {t} = useTranslation();
    const menuButtonRef = useRef(null);

    const sanitizedStatuses = useMemo(() => sanitizeStatusList(statuses), [statuses]);
    const normalizedCurrentStatus = typeof status === "string" ? status.toLowerCase() : "";
    const canonicalCurrentStatus = STATUS_ALIASES[normalizedCurrentStatus] ?? normalizedCurrentStatus;

    const currentStatusValue = useMemo(() => {
        if (sanitizedStatuses.length > 0) {
            const match = sanitizedStatuses.find(({normalized}) => normalized === canonicalCurrentStatus);
            return match?.raw ?? sanitizedStatuses[0].raw ?? DRAFT;
        }

        if (typeof status === "string" && status.trim()) {
            return status.trim();
        }

        if (canonicalCurrentStatus) {
            return canonicalCurrentStatus;
        }

        return DRAFT;
    }, [canonicalCurrentStatus, sanitizedStatuses, status]);

    const menuOptions = useMemo(() => {
        if (sanitizedStatuses.length > 0) {
            return sanitizedStatuses.filter(({normalized}) => normalized !== canonicalCurrentStatus);
        }

        const baseTransitions = DEFAULT_STATUS_TRANSITIONS[canonicalCurrentStatus]
            ?? DEFAULT_STATUS_TRANSITIONS.default;
        const seen = new Set();

        return baseTransitions.reduce((accumulator, value) => {
            if (typeof value !== "string") {
                return accumulator;
            }

            const trimmed = value.trim();
            if (!trimmed) {
                return accumulator;
            }

            const normalized = trimmed.toLowerCase();
            if (normalized === canonicalCurrentStatus || seen.has(normalized)) {
                return accumulator;
            }

            seen.add(normalized);
            const canonical = STATUS_ALIASES[normalized] ?? normalized;
            accumulator.push({raw: trimmed, normalized: canonical});
            return accumulator;
        }, []);
    }, [canonicalCurrentStatus, sanitizedStatuses]);

    const currentPresentation = resolveStatusPresentation(currentStatusValue, t);
    const CurrentIcon = currentPresentation.IconComponent;

    return (
        <Menu as="div" className="relative inline-block text-left w-full">
            <div>
                <Menu.Button
                    ref={menuButtonRef}
                    className={classNames(
                        "btn rounded-0 w-full justify-between",
                        currentPresentation.baseClass,
                        isEditable ? currentPresentation.hoverClass : "",
                    )}
                >
                    <div className="flex items-center space-x-2">
                        {isLoading ? (
                            <div className="mr-6">
                                <Loader />
                            </div>
                        ) : (
                            <CurrentIcon
                                className="w-4 h-4 mr-1 flex-shrink-0"
                                aria-hidden="true"
                            />
                        )}

                        {currentPresentation.title}
                    </div>

                    {isEditable && menuOptions.length > 0 && (
                        <ChevronDownIcon className="w-5 h-5 ml-4 -mr-1 text-current" aria-hidden="true" />
                    )}
                </Menu.Button>
            </div>

            {isEditable && menuOptions.length > 0 && (
                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <Menu.Items
                        as={StatusListDropdown}
                        buttonRef={menuButtonRef}
                        className="origin-top-right bg-solitude-200 divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                    >
                        {menuOptions.map((option) => {
                            const optionPresentation = resolveStatusPresentation(option.raw, t);
                            const OptionIcon = optionPresentation.IconComponent;
                            return (
                                <Menu.Item key={option.normalized}>
                                    {({active}) => (
                                        <div
                                            onClick={() => onChangeStatus(option.normalized ?? option.raw)}
                                            className={classNames(
                                                active ? "bg-indigo-800 text-solitude-200" : "bg-solitude-200",
                                                optionPresentation.baseClass,
                                                optionPresentation.hoverClass,
                                                "group flex items-center w-full p-3 text-sm cursor-pointer"
                                            )}
                                        >
                                            <OptionIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                                            {optionPresentation.title}
                                        </div>
                                    )}
                                </Menu.Item>
                            );
                        })}
                    </Menu.Items>
                </Transition>
            )}
        </Menu>
    );
};
